import React, { Suspense, useState } from 'react';
import { AppSidebar, type AppSidebarProps } from './AppSidebar';
import { AppTopBar } from './AppTopBar';
import type { AppSwitchProps } from './AppSwitchLink';
import { LoadingSpinner } from './LoadingSpinner';
import { useSidebarCollapse } from './useSidebarCollapse';
import { NarrowOverlayBackdrop, useNarrowOverlayDismiss } from './narrowOverlay';
import { SmallScreenGate, useIsSmallScreen, useSmallScreenGateExempt } from './SmallScreenGate';

export interface AppLayoutProps {
  sidebarProps: AppSidebarProps;
  /** Content width: 'full' = full-bleed (campaign detail), 'centered' = capped
   *  max-w-[1200px] and centered (default — home/campaigns/settings…). */
  contentVariant?: 'centered' | 'full';
  /** Bound the content to the viewport so a child scrolls internally instead
   *  of the whole page. Centered: capped to the viewport minus the 52px top bar
   *  (e.g. a `fillHeight` DataTable). Full: the shell itself becomes 100dvh with
   *  no page scroll (the outreach thread). Off by default — pages grow and the
   *  page scrolls as before. */
  contentFill?: boolean;
  /** Campaign routes set this: the rail force-collapses for the campaign view
   *  WITHOUT writing the persisted preference, so leaving the campaign restores
   *  whatever the rail was before (no global side effect). Ignored when
   *  `fixedSidebar` is set. */
  collapseRail?: boolean;
  /** The rail never expands and shows no toggle — Figma "Admin main side
   *  bar" is a permanently-narrow icon rail, not a collapsible one. Overrides
   *  `collapseRail` and the persisted collapse preference entirely. */
  fixedSidebar?: boolean;
  /** True while Firebase session + profile are loading. Shows a spinner. */
  isLoading: boolean;
  /** Campaign-detail route base for notification deep links: brand `/campaign`,
   *  admin `/campaigns`. Defaults to `/campaign`. */
  campaignRouteBase?: string;
  /** Left side of the top header — e.g. a breadcrumb (Figma "🏠 Home"). */
  headerLeft?: React.ReactNode;
  /** Right side of the top header, before the notification bell (e.g. a
   *  "Draft saved" chip on the Create Campaign flow). */
  headerRight?: React.ReactNode;
  /** Link to the other Discovr app, rendered first in the top bar's right group. */
  appSwitch?: AppSwitchProps;
  children: React.ReactNode;
}

export function AppLayout(props: AppLayoutProps) {
  const small = useIsSmallScreen();
  const exempt = useSmallScreenGateExempt();
  const gated = small && !exempt;
  return (
    <>
      <AppLayoutBody {...props} />
      {gated && <SmallScreenGate onSignOut={props.sidebarProps.onSignOut} />}
    </>
  );
}

function AppLayoutBody({
  sidebarProps,
  contentVariant = 'centered',
  contentFill = false,
  collapseRail = false,
  fixedSidebar = false,
  isLoading,
  campaignRouteBase,
  headerLeft,
  headerRight,
  appSwitch,
  children,
}: AppLayoutProps) {
  // Persisted collapse preference for normal screens. Lives in the layout (not
  // AppSidebar) so the main content margin can track the rail width.
  const pref = useSidebarCollapse(undefined, { collapseOnNarrow: true });

  // Manual expand WHILE inside a campaign — temporary, never persisted.
  const [tempExpand, setTempExpand] = useState(false);
  // Flash-free reset: clear the temporary expand DURING render the moment we
  // enter a campaign (collapseRail false→true), so a freshly-opened campaign
  // never paints an expanded rail before collapsing. Setting state during render
  // (guarded) is the React-sanctioned "adjust state on prop change" pattern — no
  // useEffect, so no extra committed paint/flash. Prev value is tracked in state
  // (not a ref) so it isn't read/written during render.
  const [prevCollapseRail, setPrevCollapseRail] = useState(collapseRail);
  if (collapseRail !== prevCollapseRail) {
    setPrevCollapseRail(collapseRail);
    if (collapseRail) setTempExpand(false);
  }

  const collapsed = fixedSidebar ? true : collapseRail ? !tempExpand : pref.collapsed;
  const onToggleCollapse = fixedSidebar
    ? undefined // the fixed rail has no toggle at all
    : collapseRail
      ? () => setTempExpand((v) => !v) // temporary, NOT persisted
      : pref.toggleCollapsed; // persisted preference

  const fillFull = contentVariant === 'full' && contentFill;

  const {
    dismiss: dismissRail,
    toggleRef: railToggleRef,
    trapRef: railTrapRef,
  } = useNarrowOverlayDismiss(!collapsed, onToggleCollapse);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    // min-h-screen is 100vh, taller than 100dvh while mobile browser chrome shows, so the bounded shell drops it too.
    <div className={fillFull ? 'relative h-dvh overflow-hidden' : 'relative min-h-screen'}>
      {!collapsed && <NarrowOverlayBackdrop onDismiss={dismissRail} />}
      <AppSidebar
        {...sidebarProps}
        collapsed={collapsed}
        onToggleCollapse={onToggleCollapse}
        expandToggleRef={railToggleRef}
        overlayRef={railTrapRef}
      />
      <main
        className={[
          fillFull ? 'box-border flex h-dvh min-h-0 flex-1 flex-col overflow-hidden' : 'box-border flex min-h-screen flex-1 flex-col',
          'bg-background transition-[margin] duration-[260ms] ease-[cubic-bezier(0.32,0.72,0,1)] will-change-[margin]',
          // The dark fixed rail (Figma "Admin main side bar") is 69px
          // (re-measured 2026-09-29, supersedes the earlier 96px/ml-24
          // pixel-match) — distinct from the plain collapsed rail's
          // unrelated 64px spec.
          // Below md an expanded rail overlays the content instead of pushing it
          // (the margin would otherwise grow the layout viewport past the screen).
          fixedSidebar ? 'ml-[69px]' : collapsed ? 'ml-[64px]' : 'ml-[64px] md:ml-56',
          'min-w-0',
        ].join(' ')}
      >
        {/* Persistent top app bar — its own component; full-width so the bell
            stays in the extreme-right corner, with the action slot (Create
            Campaign / Draft saved) to its left. */}
        <AppTopBar headerLeft={headerLeft} headerRight={headerRight} appSwitch={appSwitch} campaignRouteBase={campaignRouteBase} />
        {/* Suspense lives HERE (not at the route root) so a lazy route swap shows
            the loader ONLY in the content region — the rail + top bar stay painted
            and never remount. */}
        <Suspense
          fallback={
            <div className="flex flex-1 items-center justify-center">
              <LoadingSpinner size={32} />
            </div>
          }
        >
          {contentVariant === 'full' ? (
            // Full-bleed: campaign detail owns its own two-column spacing.
            // Bounded: the page inside lays out to fit, so this only scrolls when a viewport is too short for it (e.g. 400% zoom).
            <div className={fillFull ? 'flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto' : 'flex min-w-0 flex-1 flex-col'}>{children}</div>
          ) : (
            // Capped to the Discovr grid (1136 inner) and centered. When
            // contentFill is set, the content is bounded to the viewport (minus
            // the 52px top bar) so a fillHeight child scrolls internally instead
            // of growing the page.
            <div
              className={[
                'mx-auto flex w-full max-w-[1200px] flex-1 flex-col px-8 py-8',
                contentFill ? 'max-h-[calc(100vh-52px)] min-h-0 overflow-hidden' : '',
              ].join(' ')}
            >
              {children}
            </div>
          )}
        </Suspense>
      </main>
    </div>
  );
}
